import extend from 'lodash/extend';
import noop from 'lodash/noop';
import { Directive, EventEmitter, Inject, Input, NgZone, Output, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { MapComponent } from './map.component';
import { gmapsLoadPromise } from '../lib/google-maps';
import { generateImage, CANVAS_WIDTH, CANVAS_HEIGHT } from './utils';

type VehicleImageKeyType = 'marker-driving-eld-enabled-glow' | 'marker-driving-eld-enabled' | 'vehicle-stationary' | 'off-duty';

const MAP_ZOOM: number = 16;

@Directive({
  selector: 'current-location-marker',
})
export class CurrentLocationMarkerDirective implements OnInit, OnChanges, OnDestroy {
  @Input() public lat: number;
  @Input() public lng: number;
  @Input() public title: string;
  @Input() public type: string;
  @Input() public zIndex: number;
  @Input() public visible: boolean;
  @Input() public autoZoom: boolean = true;
  @Input() public dutyStatus: string;
  @Input() public showAlert: boolean;
  @Input() public bearing: number;
  @Input() public motionDescription: string;
  @Input() public moving: boolean;
  @Input() public panWithAnimation: boolean = true;
  @Input() public id: number | string;
  @Output() public onClick = new EventEmitter();
  @Output() public onDblClick = new EventEmitter();
  private markerObj: google.maps.Marker;
  private gmaps;
  private createMarkerPromise = new Promise((resolve, reject) => reject());

  constructor(@Inject(MapComponent) private map: MapComponent, @Inject(NgZone) private ngZone) {}

  public ngOnInit() {
    this.createMarkerPromise = this.map.createMapPromise
      .then(() => {
        this.gmaps = this.map.gmaps;
        return this.create();
      })
      ;
    this.map.markerPromiseList.push(this.createMarkerPromise);
  }

  public ngOnChanges(changes) {
    const performUpdates = () => {
      if (changes.visible != undefined) {
        this.markerObj.setVisible(!!this.visible);
        this.map.markerCluster.resetViewport();
        return;
      }

      const checkKeys: string[] = ['type', 'lat', 'lng', 'bearing', 'dutyStatus', 'motionDescription'];
      const shouldUpdate: boolean = checkKeys.some(key => changes[key]);
      if (!shouldUpdate) return;
      this.updateImage()
        .then(() => {
          if (changes.type) this.updateByType();
          if (changes.lat || changes.lng) this.updateByLocation();
        });
    };
    this.createMarkerPromise.then(performUpdates, noop);
  }

  public ngOnDestroy() {
    this.remove();
  }

  private updateByType() {
    if (this.type.startsWith('selected')) {
      this.removeMarkerFromCluster();
      this.addExistingMarkerToMap();
      if (this.autoZoom) this.map.panIn({ lat: this.lat, lng: this.lng }, MAP_ZOOM, this.panWithAnimation);
    } else {
      this.addMarkerBackToCluster();
    }
    this.markerObj.setZIndex(this.zIndex);
  }

  private updateByLocation() {
    const newPosition = this.getPosition();
    if (this.type.startsWith('selected')) {
      this.markerObj.setPosition(newPosition);
      this.map.panIn({ lat: this.lat, lng: this.lng });
      this.map.map.setZoom(MAP_ZOOM);
    } else {
      const cluster = this.map.markerCluster.getMarkersCluster(this.markerObj);
      if (cluster && cluster.getMarkers().length !== 1) {
        this.removeMarkerFromCluster();
        this.addExistingMarkerToMap();
      }
      this.markerObj.setPosition(newPosition);
      this.addMarkerBackToCluster();
      this.map.repaintCluster();
    }
  }

  private getPosition() {
    return new this.gmaps.LatLng(this.lat, this.lng);
  }

  private updateImage() {
    return this.generateImage(this.title).then((iconDrawingInfo: any) => {
      this.markerObj.setIcon({
        url: iconDrawingInfo.image,
        anchor: new (window as any).google.maps.Point(iconDrawingInfo.anchor.x, iconDrawingInfo.anchor.y),
        scaledSize: new (window as any).google.maps.Size(CANVAS_WIDTH, CANVAS_HEIGHT),
      });
      this.markerObj.setShape(iconDrawingInfo.shape);
    });
  }

  private create(): Promise<any> {
    const config = {
      position: this.getPosition(),
      zIndex: this.zIndex || undefined,
      visible: typeof this.visible === 'boolean' ? this.visible : true,
    };
    return this.generateImage(this.title).then(iconDrawingInfo => {
      this.addMarkerToMap(config, iconDrawingInfo);
    });
  }

  private remove() {
    if (!this.id) return;
    this.ngZone.runOutsideAngular(() => {
      this.gmaps.event.clearInstanceListeners(this.markerObj);
      this.map.removeMarker(this.id);
    });
  }

  private generateImage(title: string): Promise<{}> {
    let imageName = null;
    if (this.type.includes('vehicle')) imageName = this.getVehicleImageKey();
    else if (this.type.includes('asset')) imageName = this.getAssetImageKey();
    else if (this.type.includes('driver')) imageName = this.getDriverImageKey();
    else if (this.type.includes('shipper-facility')) imageName = this.type;
    return generateImage(title, imageName, this.showAlert, this.bearing);
  }

  private getDriverImageKey(): string {
    let imageKey = null;
    if (this.dutyStatus === 'driving') {
      imageKey = 'driving-without-eld';
    } else if (this.dutyStatus === 'on_duty' || this.dutyStatus === 'waiting') {
      imageKey = 'driver-on-duty';
    } else {
      imageKey = 'off-duty';
    }
    return imageKey;
  }

  private getAssetImageKey(): VehicleImageKeyType {
    if (this.bearing != null && this.moving === true && this.type === 'selected-asset') return 'marker-driving-eld-enabled-glow';
    else if (this.bearing != null && this.moving === true) return 'marker-driving-eld-enabled';
    else if (this.bearing != null && this.moving === false) return 'vehicle-stationary';
    return 'off-duty';
  }

  private getVehicleImageKey(): VehicleImageKeyType {
    if (this.bearing != null && this.motionDescription !== 'Stationary' && this.type === 'selected-vehicle') {
      return 'marker-driving-eld-enabled-glow';
    } else if (this.bearing != null && this.motionDescription !== 'Stationary') return 'marker-driving-eld-enabled';
    else if (this.bearing != null && this.motionDescription === 'Stationary') return 'vehicle-stationary';
    else return 'off-duty';
  }

  private addMarkerToMap(config, iconDrawingInfo): void {
    const { image, anchor, shape } = iconDrawingInfo;
    extend(config, {
      icon: {
        url: image,
        anchor: new (window as any).google.maps.Point(anchor.x, anchor.y),
        scaledSize: new (window as any).google.maps.Size(CANVAS_WIDTH, CANVAS_HEIGHT),
      },
      shape,
    });

    this.markerObj = new this.gmaps.Marker(config);
    const markerObj = this.markerObj;
    this.ngZone.runOutsideAngular(() => {
      this.gmaps.event.addListener(markerObj, 'click', e => {
        this.onClick.emit();
      });
      this.gmaps.event.addListener(markerObj, 'dblclick', () =>
        this.onDblClick.emit(),
      );
    });
    this.id = this.map.addMarkerToCluster(markerObj, this.id);
  }

  private removeMarkerFromCluster(): void {
    this.map.markerCluster.removeMarker(this.markerObj);
  }

  private addExistingMarkerToMap(): void {
    this.map.addExistingMarkerToMap(this.id);
  }

  private addMarkerBackToCluster(): void {
    this.map.addMarkerBackToCluster(this.id);
  }
}
